/**
 * Per-panel **"Follow live updates"** toggle for the four operator list
 * panels — Platforms, Detections, Alerts, Tracks (REQ-UI-182; operator
 * request 2026-10-01).
 *
 * The lists update live. With nothing selected, a *following* panel keeps
 * the newest rows in view (the list is pinned to its top on every update);
 * a panel that is *not* following is **paused** — it renders the rows that
 * were on screen at the flip, so nothing inserts, prunes or re-sorts under
 * the operator (:func:`@/multi-pane/use-paused-list-rows`, REQ-UI-182
 * amended 2026-10-10). A selected row is held in place while paused, and a
 * flip back to following returns to the top of the list — see
 * :func:`@/selection/use-keep-selected-row-in-view`, which consumes the flag.
 *
 * # Why session-only
 *
 * Decided 2026-10-01: the flag is a reading posture for the moment, not a
 * preference worth a Profile field — exactly like the Tracks panel's
 * expansion state (REQ-UI-170). It lives in this module-level store for the
 * life of the page, survives the panel being closed and reopened, and resets
 * to *following* on reload. Nothing here touches the BFF or the Profile
 * document (ADR-033).
 */
import { create } from "zustand";

/** The list panels that carry the toggle, by dockview panel id. */
export const FOLLOWABLE_PANEL_IDS = ["platforms", "detections", "alerts", "tracks"] as const;

/** One of :data:`FOLLOWABLE_PANEL_IDS`. */
export type FollowablePanelId = (typeof FOLLOWABLE_PANEL_IDS)[number];

/** Store shape: one flag per panel plus the mutators. */
export interface ListFollowState {
  /** ``true`` = the panel follows live updates (the default). */
  readonly follow: Readonly<Record<FollowablePanelId, boolean>>;
  /** Set one panel's flag explicitly. */
  readonly setFollow: (panelId: FollowablePanelId, value: boolean) => void;
  /** Flip one panel's flag. */
  readonly toggleFollow: (panelId: FollowablePanelId) => void;
  /** Back to every panel following — tests, and a session reset. */
  readonly reset: () => void;
}

const DEFAULT_FOLLOW: Readonly<Record<FollowablePanelId, boolean>> = {
  platforms: true,
  detections: true,
  alerts: true,
  tracks: true,
};

/**
 * The module-level follow store. Components subscribe through
 * :func:`useListFollow`; the header toggle writes through ``toggleFollow``.
 */
export const useListFollowStore = create<ListFollowState>((set) => ({
  follow: { ...DEFAULT_FOLLOW },
  setFollow: (panelId, value) =>
    set((state) => ({ follow: { ...state.follow, [panelId]: value } })),
  toggleFollow: (panelId) =>
    set((state) => ({ follow: { ...state.follow, [panelId]: !state.follow[panelId] } })),
  reset: () => set({ follow: { ...DEFAULT_FOLLOW } }),
}));

/**
 * Subscribe to one panel's follow flag.
 *
 * @param panelId - the list panel.
 * @returns whether that panel currently follows live updates.
 */
export function useListFollow(panelId: FollowablePanelId): boolean {
  return useListFollowStore((state) => state.follow[panelId]);
}
